import { apiRequest, ApiError } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

/**
 * Landing-page announcements feed, newest-first.
 * GET /api/dashboard/posts → { items: [...], canEdit } — canEdit reports whether the
 * caller may author posts (dashboard module at manage / admin); render editor affordances
 * from it, the mutations enforce it server-side regardless.
 */
export async function fetchDashboardPosts({ token } = {}) {
  const payload = await apiRequest("/api/dashboard/posts", {
    method: "GET",
    headers: createAuthHeaders(token),
  });
  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    canEdit: payload?.canEdit === true,
  };
}

export async function createDashboardPost({ title, body, token } = {}) {
  return apiRequest("/api/dashboard/posts", {
    method: "POST",
    headers: createAuthHeaders(token, { "Content-Type": "application/json" }),
    body: JSON.stringify({ title: String(title || ""), body: String(body || "") }),
  });
}

export async function updateDashboardPost(postId, { title, body, token } = {}) {
  const id = String(postId || "").trim();
  if (!id) {
    throw new ApiError("postId is required", 400);
  }
  return apiRequest(`/api/dashboard/posts/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: createAuthHeaders(token, { "Content-Type": "application/json" }),
    body: JSON.stringify({ title: String(title || ""), body: String(body || "") }),
  });
}

export async function deleteDashboardPost(postId, { token } = {}) {
  const id = String(postId || "").trim();
  if (!id) {
    throw new ApiError("postId is required", 400);
  }
  return apiRequest(`/api/dashboard/posts/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}

function requirePostId(postId) {
  const id = String(postId || "").trim();
  if (!id) {
    throw new ApiError("postId is required", 400);
  }
  return encodeURIComponent(id);
}

function requireCommentId(commentId) {
  const id = String(commentId || "").trim();
  if (!id) {
    throw new ApiError("commentId is required", 400);
  }
  return encodeURIComponent(id);
}

/**
 * One post in the exact shape a list row has. This is what lets a `reacted`/`commented`
 * realtime ping patch a single post in place instead of pulling the whole feed down again.
 */
export async function fetchDashboardPost(postId, { token } = {}) {
  return apiRequest(`/api/dashboard/posts/${requirePostId(postId)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
  });
}

/**
 * Set the caller's reaction on a post — one slot per person, so this replaces whatever they
 * held before. `emoji` must be from REACTION_QUICK_SET; the server rejects anything else.
 * Both reaction calls return the updated post view.
 */
export async function setDashboardPostReaction(postId, { emoji, token } = {}) {
  return apiRequest(`/api/dashboard/posts/${requirePostId(postId)}/reactions`, {
    method: "POST",
    headers: createAuthHeaders(token, { "Content-Type": "application/json" }),
    body: JSON.stringify({ emoji: String(emoji || "") }),
  });
}

export async function clearDashboardPostReaction(postId, { token } = {}) {
  return apiRequest(`/api/dashboard/posts/${requirePostId(postId)}/reactions`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}

/** One post's comments, oldest-first. Fetched on expand — never part of the feed payload. */
export async function fetchDashboardPostComments(postId, { token } = {}) {
  const payload = await apiRequest(`/api/dashboard/posts/${requirePostId(postId)}/comments`, {
    method: "GET",
    headers: createAuthHeaders(token),
  });
  return { items: Array.isArray(payload?.items) ? payload.items : [] };
}

export async function createDashboardPostComment(postId, { body, token } = {}) {
  return apiRequest(`/api/dashboard/posts/${requirePostId(postId)}/comments`, {
    method: "POST",
    headers: createAuthHeaders(token, { "Content-Type": "application/json" }),
    body: JSON.stringify({ body: String(body || "") }),
  });
}

/** Author-only server-side; a non-author gets 404, not 403. */
export async function updateDashboardPostComment(postId, commentId, { body, token } = {}) {
  const path = `/api/dashboard/posts/${requirePostId(postId)}/comments/${requireCommentId(commentId)}`;
  return apiRequest(path, {
    method: "PUT",
    headers: createAuthHeaders(token, { "Content-Type": "application/json" }),
    body: JSON.stringify({ body: String(body || "") }),
  });
}

/** Authors delete their own; dashboard managers delete anyone's. */
export async function deleteDashboardPostComment(postId, commentId, { token } = {}) {
  const path = `/api/dashboard/posts/${requirePostId(postId)}/comments/${requireCommentId(commentId)}`;
  return apiRequest(path, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}
